import { PauseCircle } from "lucide-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { usePauseOnlineBooking } from "../hooks/use-pause-online-booking";
import { formatPauseEnd } from "../utils/pause-status";

/** Consumed by the fixed nav bars and PageContent to offset themselves. */
const BANNER_HEIGHT_VAR = "--paused-banner-height";

/**
 * App-level strip shown on every dashboard page while online bookings are
 * paused, with a one-click resume. Intentionally not dismissible — it must stay
 * visible until the owner resumes.
 *
 * The nav bars are `position: fixed`, so they can't be pushed down by flow.
 * The banner is fixed too and publishes its own height as a CSS custom property
 * (same measure-and-report pattern the nav bars use for `navBarHeight`) that the
 * nav bars and PageContent add to their top offsets.
 */
export function PausedBookingsBanner() {
  const { isPaused, isIndefinite, pausedUntil, pausedMessage, salonTimezone, saving, resume } =
    usePauseOnlineBooking();
  const { t, i18n } = useTranslation("settings");
  // Measured without the safe-area padding, which the mobile nav bar already
  // applies itself — otherwise the inset would be counted twice.
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = document.documentElement;
    const clear = () => root.style.setProperty(BANNER_HEIGHT_VAR, "0px");

    if (!isPaused) {
      clear();
      return;
    }

    const measureHeight = () => {
      root.style.setProperty(BANNER_HEIGHT_VAR, `${contentRef.current?.offsetHeight ?? 0}px`);
    };

    measureHeight();

    const resizeObserver = new ResizeObserver(measureHeight);
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      resizeObserver.disconnect();
      clear();
    };
  }, [isPaused]);

  if (!isPaused) return null;

  const pauseEnd = formatPauseEnd(isIndefinite, pausedUntil, salonTimezone, i18n.language);

  return (
    <div
      // Above the nav bars (desktop z-30, mobile z-80) so it always stays visible.
      className="fixed top-0 left-0 right-0 z-90 border-b border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/50 dark:bg-amber-900/30 dark:text-amber-200"
      style={{ paddingTop: "var(--safe-area-inset-top, 0px)" }}
    >
      <div ref={contentRef} className="flex items-center gap-2 px-3 py-2">
        <PauseCircle className="w-4 h-4 flex-shrink-0" />
        <div className="min-w-0 flex-1 text-xs sm:text-sm">
          <span className="font-medium">
            {pauseEnd
              ? t("pauseBookings.bannerUntil", { date: pauseEnd })
              : t("pauseBookings.bannerIndefinitely")}
          </span>
          {pausedMessage && <span className="hidden sm:inline opacity-80"> — {pausedMessage}</span>}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={resume}
          disabled={saving}
          className="h-7 flex-shrink-0 border-amber-300 bg-transparent text-amber-900 hover:bg-amber-100 hover:text-amber-950 dark:border-amber-700 dark:text-amber-100 dark:hover:bg-amber-900/60 dark:hover:text-amber-50"
        >
          {saving ? t("pauseBookings.resuming") : t("pauseBookings.resumeShort")}
        </Button>
      </div>
    </div>
  );
}
